/**
 * Service to manage the last used delivery address in localStorage
 * This ensures that users see their previously selected address in new orders
 * instead of always defaulting to the institute's default address
 */

const LAST_USED_ADDRESS_KEY = 'lastUsedDeliveryAddress'

export interface LastUsedAddressData {
  shippingAddressId: string
  timestamp: number
  userId?: string // Optional: to handle multi-user scenarios
}

/**
 * Save the last used delivery address to localStorage
 */
export const saveLastUsedAddress = (
  shippingAddressId: string,
  userId?: string
): void => {
  try {
    const addressData: LastUsedAddressData = {
      shippingAddressId,
      timestamp: Date.now(),
      userId
    }
    localStorage.setItem(LAST_USED_ADDRESS_KEY, JSON.stringify(addressData))
  } catch (error) {
    console.warn('Failed to save last used address to localStorage:', error)
  }
}

/**
 * Get the last used delivery address from localStorage
 */
export const getLastUsedAddress = (userId?: string): string | null => {
  try {
    const stored = localStorage.getItem(LAST_USED_ADDRESS_KEY)
    if (!stored) {
      return null
    }

    const addressData: LastUsedAddressData = JSON.parse(stored)

    // If userId is provided, check if it matches (for multi-user scenarios)
    if (userId && addressData.userId && addressData.userId !== userId) {
      return null
    }

    // Check if the data is not too old (optional: 30 days expiry)
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000
    if (Date.now() - addressData.timestamp > thirtyDaysInMs) {
      clearLastUsedAddress()
      return null
    }

    return addressData.shippingAddressId
  } catch (error) {
    console.warn(
      'Failed to retrieve last used address from localStorage:',
      error
    )
    return null
  }
}

/**
 * Clear the last used delivery address from localStorage
 * This should only be called when explicitly needed (e.g., user logout, address no longer valid)
 */
export const clearLastUsedAddress = (): void => {
  try {
    localStorage.removeItem(LAST_USED_ADDRESS_KEY)
  } catch (error) {
    console.warn('Failed to clear last used address from localStorage:', error)
  }
}

/**
 * Check if a last used address exists
 */
export const hasLastUsedAddress = (userId?: string): boolean => {
  return getLastUsedAddress(userId) !== null
}
